import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { rupee, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  User, Package, MapPin, LogOut, Plus, Trash2, RefreshCw, Crown, Gift, Copy,
  Check, Pencil, Store, Clock, Sparkles, Truck, X,
} from "lucide-react";
import { toast } from "sonner";

const ACTIVE_STATUSES = ["placed", "accepted", "preparing", "ready", "out_for_delivery"];
const EMPTY_ADDR = { label: "Home", address: "", landmark: "", city: "", pincode: "" };

export default function Account() {
  const { user, loading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState(EMPTY_ADDR);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", email: "" });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!loading && !user) navigate("/login?next=/account"); }, [user, loading]);
  useEffect(() => {
    if (user) {
      api.get("/my/orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/my/addresses").then((r) => setAddresses(r.data)).catch(() => {});
      api.get("/my/referral").then((r) => setReferral(r.data)).catch(() => {});
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const resetForm = () => { setForm(EMPTY_ADDR); setEditingId(null); setShowForm(false); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) setProfileForm({ name: user.name || "", email: user.email || "" }); }, [user?.id]);

  const saveProfile = async () => {
    if (!profileForm.name.trim()) { toast.error("Please enter your name"); return; }
    try {
      await api.put("/my/profile", { name: profileForm.name.trim(), email: profileForm.email.trim() });
      await refreshUser();
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAddress = async () => {
    if (!form.address || !form.city || !form.pincode) { toast.error("Fill address, city and pincode"); return; }
    try {
      if (editingId) {
        const { data } = await api.put(`/my/addresses/${editingId}`, form);
        setAddresses(addresses.map((a) => (a.id === editingId ? data : a)));
        toast.success("Address updated");
      } else {
        const { data } = await api.post("/my/addresses", form);
        setAddresses([...addresses, data]);
        toast.success("Address saved");
      }
      resetForm();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const editAddress = (a) => {
    setForm({ label: a.label || "Home", address: a.address || "", landmark: a.landmark || "", city: a.city || "", pincode: a.pincode || "" });
    setEditingId(a.id); setShowForm(true);
  };
  const delAddress = async (id) => {
    await api.delete(`/my/addresses/${id}`);
    setAddresses(addresses.filter((a) => a.id !== id));
    if (editingId === id) resetForm();
    toast.success("Address removed");
    api.get("/my/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  };
  const makeDefault = async (id) => {
    await api.put(`/my/addresses/${id}/default`);
    setAddresses((prev) => prev.map((a) => ({ ...a, is_default: a.id === id })));
    toast.success("Default address updated");
  };

  const reorder = (order) => {
    order.items.forEach((it) => addItem({
      product_id: it.product_id, slug: "", name: it.name, image: it.image, variant: it.variant,
      unit_price: it.unit_price, customizations: it.customizations, add_ons: it.add_ons, quantity: it.quantity, veg: true,
    }));
    localStorage.setItem("bbs_reorder", JSON.stringify({
      order_type: order.order_type || "delivery",
      delivery_address: order.delivery_address || null,
      kitchen_id: order.kitchen?.id || null,
    }));
    if (order.delivery_address?.city) localStorage.setItem("bbs_location", order.delivery_address.city);
    toast.success("Items added — review & confirm your booking");
    navigate("/cart");
  };

  const copyReferral = () => {
    const text = referral?.link || referral?.code || "";
    if (!text) return;
    navigator.clipboard?.writeText(text);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
    toast.success("Referral link copied");
  };

  if (!user) return <div className="py-24 text-center text-muted-foreground">Loading...</div>;

  const activeBookings = orders.filter((o) => ACTIVE_STATUSES.includes(o.status) && o.payment_status !== "pending");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-12" data-testid="account-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-4 flex flex-col gap-6">
          <div className="bg-navy rounded-xl border border-gold/20 text-cream shadow-lg p-8" data-testid="profile-card">
            <div className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-full bg-gold/20 text-gold flex items-center justify-center shrink-0"><Crown size={26} /></span>
              <div className="min-w-0">
                <h1 className="font-serif text-2xl text-cream leading-tight truncate">{user.name}</h1>
                <div className="text-sm text-cream/60 truncate">{user.email || user.phone}</div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between">
              <div className="text-xs text-cream/50">{user.phone || "—"}</div>
              <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="flex items-center gap-1.5 text-sm text-cream/70 hover:text-gold transition-colors"><LogOut size={15} /> Logout</button>
            </div>
          </div>

          <div className="rounded-xl p-6 relative overflow-hidden" style={{ background: "#E7C873" }} data-testid="reward-points-banner">
            <Sparkles size={110} className="absolute -right-4 -top-6 text-navy/10" />
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-navy/70">Begum's Royal Rewards</div>
            <div className="font-serif text-4xl text-navy mt-1" data-testid="loyalty-points">{user.loyalty_points ?? 0} <span className="text-2xl">pts</span></div>
            <div className="text-xs text-navy/70 mt-2 max-w-[230px]">Earn 5% back on every delivered order · Redeem 1 pt = ₹1 at checkout</div>
          </div>

          <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6" data-testid="referral-card">
            <div className="flex items-center gap-2 mb-1"><Gift size={18} className="text-gold" /><h3 className="font-serif text-xl text-navy">Share the Secret</h3></div>
            <p className="text-sm text-muted-foreground">Refer a friend — they get a welcome coupon, and you earn a <span className="text-navy font-semibold">₹100-off</span> coupon when they complete their first order.</p>
            <div className="flex items-center gap-2 mt-4">
              <div className="flex-1 bg-beige rounded-full px-4 py-2.5 text-sm font-semibold text-navy truncate" data-testid="referral-code">{referral?.code || "…"}</div>
              <button onClick={copyReferral} data-testid="referral-copy-btn" className="shrink-0 flex items-center gap-1.5 bg-navy text-cream text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-navy-soft transition-colors active:scale-95">{copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy"}</button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="text-center bg-beige/60 rounded-lg py-3"><div className="font-serif text-2xl text-navy" data-testid="referral-count">{referral?.total_referred ?? 0}</div><div className="text-xs text-muted-foreground">Friends referred</div></div>
              <div className="text-center bg-beige/60 rounded-lg py-3"><div className="font-serif text-2xl text-navy" data-testid="referral-rewards">{referral?.rewards_earned ?? 0}</div><div className="text-xs text-muted-foreground">Rewards earned</div></div>
            </div>
            {referral?.welcome_coupon && (
              <div className="mt-3 text-xs bg-gold/10 text-navy rounded-lg px-3 py-2" data-testid="welcome-coupon">Your welcome coupon: <b>{referral.welcome_coupon}</b> — use it at checkout!</div>
            )}
            {referral?.reward_coupons?.length > 0 && (
              <div className="mt-3 text-xs text-muted-foreground">Your reward coupons: {referral.reward_coupons.map((c) => <span key={c} className="inline-block bg-beige rounded px-2 py-0.5 mr-1 mb-1 text-navy font-medium">{c}</span>)}</div>
            )}
          </div>
        </aside>

        {/* Main */}
        <section className="lg:col-span-8">
          <Tabs defaultValue="bookings">
            <TabsList className="bg-transparent border-b border-black/10 rounded-none w-full justify-start gap-1 p-0 h-auto overflow-x-auto flex-nowrap scrollbar-hide">
              <TabTrig value="bookings" testid="tab-bookings" icon={Truck} label="Active Bookings" />
              <TabTrig value="orders" testid="tab-orders" icon={Package} label="Order History" />
              <TabTrig value="addresses" testid="tab-addresses" icon={MapPin} label="Saved Addresses" />
              <TabTrig value="profile" testid="tab-profile" icon={User} label="Profile" />
            </TabsList>

            {/* Active Bookings */}
            <TabsContent value="bookings" className="mt-6 space-y-4">
              {activeBookings.length === 0 ? (
                <Empty text="No active bookings right now." testid="no-bookings" />
              ) : activeBookings.map((o) => (
                <div key={o.id} className="bg-white rounded-xl border border-black/5 shadow-sm p-6" data-testid={`booking-${o.order_number}`}>
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2"><span className="font-semibold text-navy">{o.order_number}</span>
                        <span className="text-[11px] uppercase tracking-wide bg-navy/5 text-navy px-2 py-0.5 rounded-full flex items-center gap-1">{o.order_type === "takeaway" ? <><Store size={11} /> Pickup</> : <><Truck size={11} /> Delivery</>}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{new Date(o.created_at).toLocaleString("en-IN")}</div>
                    </div>
                    <span className="text-xs bg-gold/15 text-navy px-3 py-1 rounded-full capitalize font-medium">{o.status.replace(/_/g, " ")}</span>
                  </div>
                  {o.order_type === "takeaway" && o.kitchen && (
                    <div className="mt-3 bg-beige/60 rounded-lg p-3 text-sm">
                      <div className="font-semibold text-navy flex items-center gap-1.5"><Store size={14} className="text-gold" /> {o.kitchen.name}</div>
                      <div className="text-muted-foreground text-xs mt-0.5">{o.kitchen.address}</div>
                      <div className="text-navy text-xs mt-1 flex items-center gap-1"><Clock size={12} className="text-gold" /> Ready in {o.kitchen.ready_time}</div>
                    </div>
                  )}
                  <div className="text-sm text-muted-foreground mt-3">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</div>
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-dashed border-black/10">
                    <span className="font-semibold text-navy">{rupee(o.grand_total)}</span>
                    <Link to={`/order/${o.id}`} className="text-xs font-semibold text-cream bg-navy px-4 py-1.5 rounded-full hover:bg-navy-soft">Track Order</Link>
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* Order History */}
            <TabsContent value="orders" className="mt-6 space-y-4">
              {orders.length === 0 ? (
                <Empty text={<>No orders yet. <Link to="/menu" className="text-navy font-semibold">Start ordering →</Link></>} testid="no-orders" />
              ) : orders.map((o) => (
                <div key={o.id} className="bg-white rounded-xl border border-black/5 shadow-sm p-6 transition-all hover:shadow-md" data-testid={`order-${o.order_number}`}>
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2"><span className="font-semibold text-navy">{o.order_number}</span>
                        <span className="text-[11px] uppercase tracking-wide bg-navy/5 text-navy px-2 py-0.5 rounded-full">{o.order_type === "takeaway" ? "Pickup" : "Delivery"}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{new Date(o.created_at).toLocaleString("en-IN")}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-gold/15 text-navy px-2.5 py-1 rounded-full capitalize">{o.status.replace(/_/g, " ")}</span>
                      <span className="text-xs bg-beige px-2.5 py-1 rounded-full capitalize">{o.payment_status}</span>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</div>
                  {o.points_earned > 0 && <div className="text-xs text-gold mt-1">+ {o.points_earned} loyalty points earned</div>}
                  <div className="gold-rule my-3" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-navy">{rupee(o.grand_total)}</span>
                    <div className="flex gap-2">
                      <button onClick={() => reorder(o)} data-testid={`reorder-${o.order_number}`} className="flex items-center gap-1 text-xs font-semibold text-navy border border-navy/20 px-3.5 py-1.5 rounded-full hover:border-gold transition-colors active:scale-95"><RefreshCw size={13} /> Reorder</button>
                      <Link to={`/order/${o.id}`} className="text-xs font-semibold text-cream bg-navy px-3.5 py-1.5 rounded-full hover:bg-navy-soft">View / Invoice</Link>
                    </div>
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* Saved Addresses */}
            <TabsContent value="addresses" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {addresses.map((a) => (
                  <div key={a.id} className="bg-white rounded-xl border border-black/5 shadow-sm p-5 flex justify-between gap-3" data-testid={`address-${a.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-navy text-sm flex items-center gap-1.5"><MapPin size={14} className="text-gold" /> {a.label}
                        {a.is_default && <span className="text-[10px] bg-gold/20 text-navy px-2 py-0.5 rounded-full" data-testid={`default-badge-${a.id}`}>Default</span>}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">{a.address}{a.landmark ? `, ${a.landmark}` : ""}, {a.city} - {a.pincode}</div>
                      {!a.is_default && <button onClick={() => makeDefault(a.id)} data-testid={`set-default-${a.id}`} className="text-xs font-semibold text-navy hover:text-gold mt-1.5">Set as default</button>}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button onClick={() => editAddress(a)} data-testid={`edit-address-${a.id}`} className="text-muted-foreground hover:text-navy"><Pencil size={15} /></button>
                      <button onClick={() => delAddress(a.id)} data-testid={`delete-address-${a.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
                {!showForm && (
                  <button onClick={() => { setForm(EMPTY_ADDR); setEditingId(null); setShowForm(true); }} data-testid="add-address-card"
                    className="border-2 border-dashed border-black/15 rounded-xl p-5 flex items-center justify-center gap-2 text-navy font-semibold hover:border-gold transition-colors min-h-[96px]">
                    <Plus size={18} /> Add New Address
                  </button>
                )}
              </div>

              {showForm && (
                <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 mt-4" data-testid="address-form">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-serif text-xl text-navy">{editingId ? "Edit Address" : "Add Address"}</h3>
                    <button onClick={resetForm} className="text-muted-foreground hover:text-navy"><X size={18} /></button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Inp ph="Label (Home/Office)" v={form.label} on={(v) => setForm({ ...form, label: v })} t="addr-label-input" />
                    <Inp ph="Address" v={form.address} on={(v) => setForm({ ...form, address: v })} t="addr-address-input" />
                    <Inp ph="Landmark (optional)" v={form.landmark} on={(v) => setForm({ ...form, landmark: v })} t="addr-landmark-input" />
                    <Inp ph="City" v={form.city} on={(v) => setForm({ ...form, city: v })} t="addr-city-input" />
                    <Inp ph="Pincode" v={form.pincode} on={(v) => setForm({ ...form, pincode: v })} t="addr-pincode-input" />
                  </div>
                  <button onClick={saveAddress} data-testid="save-address-btn" className="mt-4 flex items-center gap-1.5 bg-navy text-cream font-semibold px-6 py-2.5 rounded-full hover:bg-navy-soft transition-colors active:scale-95"><Check size={16} /> {editingId ? "Update Address" : "Save Address"}</button>
                </div>
              )}
            </TabsContent>

            {/* Profile */}
            <TabsContent value="profile" className="mt-6">
              <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 max-w-md" data-testid="profile-edit">
                <h3 className="font-serif text-xl text-navy mb-4">Your Profile</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Full Name</label>
                    <input value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} data-testid="profile-name-input" className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Email</label>
                    <input value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} placeholder="you@example.com" data-testid="profile-email-input" className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
                  </div>
                  <ProfileRow label="Mobile" value={user.phone || "—"} />
                  <ProfileRow label="Loyalty Points" value={`${user.loyalty_points ?? 0} pts`} />
                  <ProfileRow label="Referral Code" value={referral?.code || "—"} />
                </div>
                <div className="flex items-center gap-3 mt-5">
                  <button onClick={saveProfile} data-testid="save-profile-btn" className="flex items-center gap-1.5 bg-navy text-cream font-semibold px-6 py-2.5 rounded-full hover:bg-navy-soft transition-colors active:scale-95"><Check size={16} /> Save Changes</button>
                  <button onClick={() => { logout(); navigate("/"); }} data-testid="profile-logout-btn" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-destructive transition-colors"><LogOut size={15} /> Logout</button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </div>
  );
}

function TabTrig({ value, testid, icon: Icon, label }) {
  return (
    <TabsTrigger value={value} data-testid={testid}
      className="shrink-0 rounded-none border-b-2 border-transparent bg-transparent px-3 sm:px-4 py-3 text-sm text-muted-foreground data-[state=active]:border-gold data-[state=active]:text-navy data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold">
      <Icon size={15} className="mr-1.5" /> <span className="hidden sm:inline">{label}</span><span className="sm:hidden">{label.split(" ")[label.split(" ").length - 1]}</span>
    </TabsTrigger>
  );
}
function Empty({ text, testid }) {
  return <div className="text-center py-14 text-muted-foreground bg-white rounded-xl border border-black/5" data-testid={testid}>{text}</div>;
}
function Inp({ ph, v, on, t }) {
  return <input placeholder={ph} value={v} onChange={(e) => on(e.target.value)} data-testid={t} className="border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />;
}
function ProfileRow({ label, value }) { return <div className="flex justify-between border-b border-dashed border-black/10 pb-2"><span className="text-muted-foreground text-sm">{label}</span><span className="text-navy font-medium text-sm">{value}</span></div>; }
